import { EventEmitter, inject, Injectable } from '@angular/core';
import {
  DialogContentService,
  IPerson as IAaNgPerson,
  ISTPersonActivity,
} from 'aa-ng';
import { Observable, Subscription } from 'rxjs';
import { IActivityCollaborator, IPerson, mapPerson } from 'src/app/models';

import { ActivityCollaboratorsModalComponent } from '../components/activity-collaborators-modal/activity-collaborators-modal.component';
import { IResultSet } from '../models';
import { PeopleService } from './people.service';

/**
 * The part of an activity-list component this service drives.
 *
 * Structural rather than a named component type, because the three dialogs that need it are different
 * library components that happen to expose the same four members.
 */
export interface ILazyScholarHost {
  activities: ISTPersonActivity[];
  lazyLoadScholars: boolean;
  maxScholarsToDisplay: number | null;
  /**
   * Toggled, not set, after `contributors` is mutated in place on one of `activities`' rows -
   * `aa-activity`'s change detection is keyed on the activity object reference, which that mutation
   * does not replace. See `ActivitiesListComponent.forceMapPeople` (AMS's `activities-list.component.ts`
   * does the same thing with `this.forceMapPeople = !this.forceMapPeople`).
   *
   * Optional until `aa-ng-d3` publishes `PeopleCollaborativeActivitiesComponent.forceMapPeople` -
   * remove the `?` once EDS is on a package version that has it.
   */
  forceMapPeople?: boolean;
  showAllPeopleChange: EventEmitter<ISTPersonActivity>;
  showAllScholarsClick: EventEmitter<ISTPersonActivity>;
}

/**
 * How many collaborators a work shows inline before it offers the full paged list.
 *
 * Shared because two things have to agree on it: the number the row renders, and the page size the
 * fetch below asks for.
 */
export const MAX_SCHOLARS_TO_DISPLAY = 10;

/**
 * Fetching and displaying one activity's collaborators, for every list that shows them.
 *
 * Simpler than the AMS equivalent by one whole mechanism: AMS has to decide which store backs each
 * activity and, on the faculty-profile branch, name the owning scholar, because that view is grained
 * per (owning person x co-author). EDS reads only the single-tenant store, so the activity guid is
 * always enough.
 *
 * What deliberately stays with each caller is assigning the result into its own collection - that is
 * the part that genuinely differs between the renderers.
 */
@Injectable({ providedIn: 'root' })
export class ActivityCollaboratorsService {
  private peopleService = inject(PeopleService);
  private dialogContentService = inject(DialogContentService);

  /**
   * The first page of an activity's collaborators, for inline display.
   *
   * Asks for one more than is displayed, which is how the row knows there is a next page and offers the
   * full list at all.
   */
  fetchFirstPage(
    activity: ISTPersonActivity,
    maxToDisplay: number = MAX_SCHOLARS_TO_DISPLAY,
  ): Observable<IResultSet<IActivityCollaborator[]>> {
    return this.peopleService.getCollaboratorsByActivityGuidPaged(
      activity.guid,
      {
        pageIndex: 0,
        pageSize: maxToDisplay + 1,
        sortProperty: 'authorOrder',
        sortDescending: false,
      },
    );
  }

  /**
   * A page of collaborators in the shape an activity row renders.
   *
   * The only real difference is the middle-name field: the endpoint and the shared collaborator table
   * both call it `midName`, while `aa-activity` reads `middleName`.
   */
  toContributors(rows: IActivityCollaborator[]): IAaNgPerson[] {
    return (rows ?? []).map((collaborator) =>
      mapPerson({
        ...collaborator,
        middleName: collaborator.midName,
      } as unknown as IPerson),
    );
  }

  /**
   * Turns on paged collaborators for an activity list that was created dynamically.
   *
   * The dialogs are built through `DialogContentService`, so there is no template to put bindings in -
   * their inputs are assigned and their outputs subscribed here instead. Three dialogs need exactly
   * this, which is why it lives in one place.
   *
   * Returns the subscriptions so the host can dispose of them with its own.
   */
  wireLazyScholars(componentRef: ILazyScholarHost): Subscription[] {
    componentRef.lazyLoadScholars = true;
    componentRef.maxScholarsToDisplay = MAX_SCHOLARS_TO_DISPLAY;

    return [
      componentRef.showAllPeopleChange.subscribe(
        (activity: ISTPersonActivity) =>
          this.loadInlineCollaborators(componentRef, activity),
      ),
      componentRef.showAllScholarsClick.subscribe(
        (activity: ISTPersonActivity) => this.openModal(activity),
      ),
    ];
  }

  private loadInlineCollaborators(
    componentRef: ILazyScholarHost,
    activity: ISTPersonActivity,
  ): void {
    this.fetchFirstPage(activity).subscribe({
      next: (resultSet) => {
        const row = componentRef.activities?.find(
          (a) =>
            a.id === activity.id &&
            a.activityTypeId === activity.activityTypeId,
        );

        if (row) {
          row.contributors = this.toContributors(resultSet?.results);

          // Reassigned so the list re-reads its rows; mutating one in place does not trip change
          // detection through the dynamic component's input.
          componentRef.activities = [...componentRef.activities];

          // `row` keeps its identity even after the reassignment above, so aa-activity - whose
          // change detection is keyed on that reference - never notices the mutated contributors.
          // Toggling this forces ActivityComponent.mapPeople to re-run. No-ops until the host forwards
          // it (see the forceMapPeople doc comment on ILazyScholarHost).
          if ('forceMapPeople' in componentRef) {
            componentRef.forceMapPeople = !componentRef.forceMapPeople;
          }
        }
      },
      error: (err) => console.error('inline collaborators error', err),
    });
  }

  /** Hands the whole collaborator list to the paged modal. */
  openModal(activity: ISTPersonActivity): void {
    this.dialogContentService
      .setDialogContent<ActivityCollaboratorsModalComponent>({
        component: ActivityCollaboratorsModalComponent,
        dialogClasses: 'medium padded',
        title: 'Activity Collaborators Dialog',
      })
      .subscribe((componentRef) => {
        componentRef.activity = activity;
      });
  }
}
